import { NGXLogger } from 'ngx-logger';
import { Injectable } from '@angular/core';
import { SubscriberService } from '@services/subscriber/subscriber.service';
import { environment } from '@env';
import { CommsService } from '@services/comms/comms.service';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { AuthService } from '@services/auth/auth.service';
import { KeycloakSsoService } from '@services/auth/keycloak-sso.service';
import { StorageService } from '@services';
import { each, get, last } from 'lodash';

@Injectable({
  providedIn: 'root'
})
export class InitializeService {

  public isInitialized = false;

  private configTokens = [
    'subscriberID',
    'endpoint',
    'servicePath',
    'subscriberName',
    'SSEURL',
    'brand',
    'enableTracing'
  ];

  constructor(
    private logger: NGXLogger,
    private subscriberService: SubscriberService,
    private authService: AuthService,
    private http: HttpClient,
    private commsService: CommsService,
    private storageService: StorageService,
    private keycloakSsoService: KeycloakSsoService
  ) {
  }

  public async initialize(): Promise<boolean | void> {
    const theURL = new URL(window.location.href);
    const newToken = theURL.searchParams.get('token');


    if (this.isInitialized) {
      this.logger.log('Already initialized');
      return Promise.resolve(true);
    } else {
      this.logger.log('Initializing');
      this.logger.debug('checking the subscriber ID');
      await this.checkSubscriberID();
      if (!newToken) {
        this.logger.debug('reloading comms token if one is available');
        this.storageService.restoreToken();
      }
      this.logger.debug('refreshing the subscriber configuration from the backend');
      await this.subscriberService.refresh();
      // On an OIDC redirect-back, exchange the ?code= for tokens BEFORE the
      // router boots and strips the query params (otherwise tryLogin finds no
      // code). Runs here, after refresh(), because the SSO client config now
      // comes from the per-subscriber ssoDescriptor the refresh just fetched —
      // it can't be a second independent APP_INITIALIZER since Angular runs
      // multi APP_INITIALIZERs concurrently, which would race this descriptor.
      // Gated on the descriptor being present so non-SSO subscribers never
      // touch the OIDC client, and on code+state so normal loads are untouched.
      // Fails gracefully: an exchange error resolves false and the app boots
      // to normal login.
      await this.tryKeycloakCodeExchange();
      this.isInitialized = true;
      this.logger.debug('initialization complete');
    }
  }

  private async tryKeycloakCodeExchange(): Promise<void> {
    const descriptor = this.subscriberService.subInfo?.ssoDescriptor;
    if (!descriptor?.issuer || !descriptor?.clientId) { return; }
    const q = new URLSearchParams(window.location.search);
    if (!q.has('code') || !q.has('state')) { return; }
    this.keycloakSsoService.configure(descriptor);
    await this.keycloakSsoService.tryLogin().catch(() => false);
  }

  public reset(): void {
    this.isInitialized = false;
  }

  public async checkSubscriberID() {
    return new Promise((resolve, reject) => {
      if (environment.data.subscriberID === 0) {
        let baseHref: string = environment.baseHref;
        if (last(baseHref) !== '/') {
          baseHref += '/';
        }

        const url = `${location.origin}${baseHref}assets/config/settings.json`;

        this.http.get(url).subscribe((response: any) => {
          each(this.configTokens, (name) => {
            const l = get(response, name);
            if (l) {
              if (name === 'subscriberID') {
                environment.data.subscriberID = +l;
              } else {
                environment.data[name] = l;
              }
            }
          });
          this.commsService.init({noAuth: () => this.authService.handleNoAuth()});
          resolve(true);
        }, (error: HttpErrorResponse) => {
          reject(error);
        });
      } else {
        this.commsService.init({noAuth: () => this.authService.handleNoAuth()});
        resolve(true);
      }
    });
  }

}
